import { Component, OnInit, OnChanges, Input, SimpleChanges, OnDestroy } from '@angular/core';

// services
import { MasterService } from '../../../../../../services/master.service'
import { StoreService } from '../../../../../../services/store.service'
import { MessageService } from '../../../../../../services/message.service'
import * as languageLibrary from '../../../../../../services/language'
import { FormControl, FormGroup, Validators } from "@angular/forms";
import moment from 'moment'

import { SALE_TYPES, SAVE_DEBOUNCE_MILLIS } from 'src/app/constants/sale';
import { formatCurrency, formatPercent, formatCount } from 'src/app/utils/format';
import { clean } from 'src/app/utils/numeric';
import { Payment } from 'src/app/models/payment.model';
import { SaleLienholder } from 'src/app/models/sale-lienholder.model';
import { PaymentCycle } from 'src/app/models/payment-cycle.model';
import { Subscription } from "rxjs";
import { Terms } from 'src/app/models/terms.model';


@Component({
    selector: 'terms',
    templateUrl: './terms.component.html',
    styleUrls: ['./terms.component.scss'],
    standalone: false
})
export class TermsComponent implements OnInit, OnChanges, OnDestroy {

    /**  */
    @Input() isUnLock: boolean = true

    /**  */
    @Input() saleId = null

    @Input() saleType: number = 0

    /** Lot presets from the corporate setup screen.  */
    @Input() presets = null

    /** The sale date or the last down/deferred payment date, if any */
    @Input() lastDeferredPayment: string | null = null

    /** Contract/Interest start date */
    protected sold: string | null = null

    /** Current sales tax method for BHPH deal. */
    protected taxMethod: string | null = null

    /**  */
    protected baseSalePrice: number = 0

    /**  */
    protected totalSalePrice: number = 0

    /**  */
    protected driveoutPrice: number = 0

    /**  */
    protected tradeAllowance: number = 0

    /**  */
    protected tradePayoff: number = 0

    /**  */
    protected salesTax: number = 0

    /**  */
    protected amountToFinance: number = 0

    /** The primary buyer or lot zip code. */
    protected saleZip: string | null = null

    /** Unique internal identity of the retail vehicle being purchased. */
    protected saleVehicleId: number | null = null

    /** The purchased vehicle year of manufacture. */
    protected vehicleYear: number | null = null

    //
    protected lienholders: SaleLienholder[] = []

    //
    protected paymentsList: Payment[] = []

    //  An associative array of sale types with the id as the key
    protected saleTypes: Record<number, string> = SALE_TYPES

    //
    protected maxApr: number = -1 // 0 is a valid value, -1 indicates not set

    //
    protected stateMaxApr: number = -1 // 0 is a valid value, -1 indicates not set

    //
    protected presetMaxApr: number = -1 // 0 is a valid value, -1 indicates not set

    // save the language
    protected language: string | null = localStorage.getItem('language') ? localStorage.getItem('language') : 'EN'

    // set all words
    protected words = languageLibrary.language

    protected initialized: boolean = false

    // define loading status
    protected loading: boolean = false

    //
    protected lotId: number = 0

    protected frequencies: PaymentCycle[] = []

    protected calculating: boolean = false

    private solveFor: string = 'payment'

    /** A setTimeout watcher to delay computations during times of rapid changes (first load) */
    private watcher: ReturnType<typeof setTimeout> | null = null

    /** A setTimeout watcher to delay computations during times of rapid changes to the financed amount */
    private financedWatcher: ReturnType<typeof setTimeout> | null = null

    /** Listen for component events and make decisions. */
    private componentWatcher: Subscription | null = null

    private subscriptions: Subscription[] = []

    //
    protected terms: FormGroup = new FormGroup({
        id: new FormControl<number>(0, [Validators.required]),
        financed: new FormControl<number>(0, [Validators.required]),
        interest: new FormControl<number>(0, []),
        apr: new FormControl<string>('18.00', [Validators.required]),
        maxApr: new FormControl<number>(0, []),
        paymentCycleId: new FormControl<number>(0, [Validators.required]),
        term: new FormControl<number>(1, [Validators.required]),
        payment: new FormControl<string>('0.00', []),
        final: new FormControl<number>(0, []),
        first: new FormControl<string>(moment().format('yyyy-MM-DD'), []),
        maturity: new FormControl<string | null>(null, []),
        payments: new FormControl<number>(0, []),
        months: new FormControl<number>(0, []),
        dealerAPRLimit: new FormControl<number>(0, []),
        contractRate: new FormControl<number>(0, []),
        dailyAPR: new FormControl<number>(0, []),
        taxRatio: new FormControl<number>(0, []),
        totalDownPayment: new FormControl<number>(0, []),
        taxRate: new FormControl<number>(0, [])
    })

    protected dealerAPRLimit: number = 0;

    protected availableToSelect: boolean = true;

    protected clean = clean

    get outside() { return ((this.saleType) && (this.saleTypes[this.saleType] === 'Outside')) }
    get bhph() { return ((this.saleType) && (this.saleTypes[this.saleType] === 'BHPH')) }
    get apr() { return clean(this.terms.get('apr').value) }

    /** Indicates if saleType supports/needs terms */
    get termable(): boolean { return ((this.saleType > 0) && (['Outside', 'BHPH'].includes(this.saleTypes[this.saleType]))) }

    /**
    * Helper method to consistently get the list of form fees as an Array.
    */
    get taxDeferred() {
        return ((this.taxMethod || '').toLocaleUpperCase() === 'DEFERRED');
    }

    get rawTerms() {
        const meta = JSON.parse(JSON.stringify(this.terms.getRawValue()))
        meta.payment = clean(meta.payment)
        meta.apr = clean(meta.apr)

        return meta
    }

    /**
     *
     * @param master
     * @param ms
     * @param store
     */
    constructor(private master: MasterService, private ms: MessageService, private store: StoreService) { }

    ngOnInit() {
        this.readFrequencies()
        this.fetchLienholders()
        this.readTerms()
        this.listen()

        this.componentWatcher = this.ms.channelComponents$.subscribe((event) => {
            if (!event || !event.message) {
                return
            }

            if (this.loading) {
                return
            }

            if (!this.initialized) {
                return
            }

            switch (event.message) {
                case 'saleRead':
                    this.baseSalePrice = event.value.baseSalePrice
                    this.totalSalePrice = event.value.totalSalePrice
                    this.driveoutPrice = event.value.driveOutPrice
                    this.sold = event.value.sold

                    if (!this.lastDeferredPayment) {
                        this.lastDeferredPayment = this.sold
                        this.computeFirstPayDate()
                    }

                    break
                case 'salePriceChanged':
                case 'totalSalePriceChanged':
                    this.baseSalePrice = event.value.baseSalePrice
                    this.totalSalePrice = event.value.totalSalePrice
                    this.driveoutPrice = event.value.driveOutPrice
                    break
                case 'soldChanged':
                    this.sold = event.value
                    if (!this.lastDeferredPayment) {
                        this.lastDeferredPayment = event.value
                        this.computeFirstPayDate()
                    }
                    break
                case 'taxMethodChanged':
                    this.taxMethod = event.value

                    let financed = this.amountToFinance
                    if (this.taxDeferred) {
                        financed = this.amountToFinance - this.salesTax
                    }

                    if (this.terms.get('financed').value != financed) {
                        this.terms.get('financed').setValue(financed, { emitEvent: false })
                        this.terms.get('financed').markAsDirty({ emitEvent: false })
                        this.calculate()
                    }

                    break
                case 'financedChanged':
                    console.log(`TermsComponent.ngOnInit: financedChanged event received, value=${event.value}`)
                    if (this.financedWatcher) {
                        clearTimeout(this.financedWatcher)
                        this.financedWatcher = null
                    }

                    if ((event.value === null) || (event.value === undefined)) {
                        return
                    }

                    this.financedWatcher = setTimeout(() => {
                        const financed = clean(event.value)
                        if (this.amountToFinance.toFixed(2) === (financed).toFixed(2)) { // no real change?
                            return
                        }

                        this.amountToFinance = financed
                        if (this.amountToFinance <= 0) {
                            console.log(`TermsComponent.financedChanged: amountToFinance ${this.amountToFinance} is <= 0, skipping calculations.`)
                            this.zero()
                            this.save()
                            return
                        }

                        this.terms.get('financed').setValue(this.amountToFinance, { emitEvent: false })
                        this.terms.get('financed').markAsDirty({ emitEvent: false })
                        this.calculate()
                    }, SAVE_DEBOUNCE_MILLIS)
                    break
                case 'vehicleRead':
                case 'vehicleAdded':
                case 'vehicleChanged':
                    this.saleVehicleId = event.value.id
                    this.vehicleYear = event.value.year
                    break
                case 'vehicleDiscarded':
                    this.vehicleYear = null
                    break
                case 'buyerRead':
                case 'buyerAdded':
                case 'buyerChanged':
                    this.saleZip = event.value.zip
                    break
                case 'buyerDiscarded':
                    this.saleZip = null
                    break
                case 'buyerAddressChanged':
                    this.saleZip = event.value.zipCode
                    break
                case 'ttlRead':
                    this.salesTax = event.value.salesTax
                    break
                case 'ttlChanged':
                    console.log(`TermsComponent.ngOnInit: ttlChanged event received, salesTax=${event.value.salesTax}`)
                    this.salesTax = event.value.salesTax
                    break
                case 'lotChanged':
                    if (this.lotId == event.value) {
                        return
                    }

                    this.lotId = event.value
                    break
            }
        })
    }

    ngOnChanges(changes: SimpleChanges): void {
        //Called before any other lifecycle hook. Use it to inject dependencies, but avoid any serious work here.
        //Add '${implements OnChanges}' to the class.
        if (!changes) {
            return
        }

        if (changes.isUnLock) {
            this.terms.disable({ emitEvent: false })
            if (this.isUnLock) {
                this.terms.enable({ emitEvent: false })
            }
        }

        const first = Object.values(changes).some(c => c.isFirstChange())
        if (first) {
            return
        }

        if (changes.saleId) {
            const identity = parseInt(this.saleId)
            if (isNaN(identity) || (identity <= 0)) {
                return
            }

            this.fetchLienholders()
            this.readTerms()
        }

        if (changes.saleType) {
            this.lienholders = []
            this.fetchLienholders()

            if (!this.termable) {
                console.log(`TermsComponent.ngOnChanges: saleType ${this.saleType} is not termable, zeroing terms and saving.`)
                this.zero()
                this.save()
            }
        }

        if (changes.presets) {
            if (!this.terms.get('paymentCycleId').value) {
                this.terms.get('paymentCycleId').setValue(this.toCycleId(this.presets['defaultBHPHPaymentCycle']), { emitEvent: false })
            }
        }

        if (changes.lastDeferredPayment) {
            this.computeFirstPayDate()
        }
    }

    ngOnDestroy() {
        for (const sub of this.subscriptions) {
            sub.unsubscribe()
        }

        if (this.componentWatcher) {
            this.componentWatcher.unsubscribe()
        }

        if (this.watcher) {
            clearTimeout(this.watcher)
        }
    }

    /**
     * 
     */
    zero = (): void => {
        this.amountToFinance = 0

        this.terms.patchValue({
            financed: 0,
            interest: 0,
            apr: this.termable ? '18.00' : '0.00',
            maxApr: 0,
            dailyAPR: 0,
            contractRate: 0,
            taxRatio: 0,
            payment: '0.00',
            final: 0,
            payments: 0,
            term: 1,
            months: 0,
            first: moment().format('yyyy-MM-DD'),
            maturity: moment().format('yyyy-MM-DD'),
            paymentCycleId: 1 // default to monthly
        }, { emitEvent: false })
    }

    /**
     * Listen for changes to user inputs after the initial loaded.
     */
    listen = (): void => {
        this.subscriptions.push(
            this.terms.get('term').valueChanges.subscribe((term) => {
                if (!this.initialized) {
                    return
                }

                if (this.watcher) {
                    clearTimeout(this.watcher)
                }

                this.watcher = setTimeout(() => {
                    this.ms.channelComponents$.emit({ message: 'termChanged', section: 'terms', value: term })
                }, SAVE_DEBOUNCE_MILLIS)
            })
        )
    }

    /**
    * Get a list of lienholders attched to this sale.
    */
    private fetchLienholders = () => {
        this.lienholders = []
        const identity = parseInt(this.saleId)
        if (isNaN(identity) || (identity <= 0)) {
            return
        }

        this.master.get(`sales/${identity}/lienholders`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.lienholders = res.data.saleLienholders || []
        })
    }

    private readFrequencies = () => {
        this.master.get(`collections/paymentcycles`, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.frequencies = res.data.paymentCycles

            if (this.saleId) { // will get frequency from readTerms
                return
            }

            if (!this.presets || !this.presets['defaultBHPHPaymentCycle']) {
                return
            }

            const defaultCycleId = this.toCycleId(this.presets['defaultBHPHPaymentCycle'])
            if (!defaultCycleId) {
                return
            }

            this.terms.get('paymentCycleId').setValue(defaultCycleId, { emitEvent: false })
            this.computeFirstPayDate()
        })
    }

    /**
     *
     */
    private readTerms = async () => {
        const identity = parseInt(this.saleId)
        if (isNaN(identity) || (identity <= 0)) {
            return
        }

        this.loading = true
        this.initialized = false
        await this.master.get(`sales/${identity}/terms`, async (res) => {
            this.loading = false

            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            const meta: Terms = res.data.saleTerms
            if (!meta || !meta.id) {
                return
            }

            // loan terms
            this.terms.patchValue({
                id: meta.id,
                paymentCycleId: meta.paymentCycleId,
                payment: formatCurrency(meta.schedulePmtAmt),
                apr: formatPercent(meta.APR || 0),
                maxApr: meta.maxApr,
                financed: meta.amountToFinance || 0,
                interest: meta.financeCharges || 0,
                final: meta.lastPmtAmt || 0,
                term: meta.numberOfPmt || 1,
                months: meta.months,
                first: meta.firstScheduledPmtDue ? moment(meta.firstScheduledPmtDue).format('yyyy-MM-DD') : moment().format('yyyy-MM-DD'),
                maturity: meta.maturity ? moment(meta.maturity).format('yyyy-MM-DD') : moment().format('yyyy-MM-DD'),
                payments: meta.totalPaymentAmount || 0,
                dealerAPRLimit: meta.dealerAPRLimit,
                contractRate: meta.contractRate,
                dailyAPR: meta.dailyAPR,
                taxRatio: meta.taxRatio,
                totalDownPayment: 0,
                taxRate: meta.taxRate
            }, { emitEvent: false })

            this.amountToFinance = meta.amountToFinance + meta.taxToFinance
            if (meta.sale) {
                this.saleType = meta.sale.saleFinancingTypeId
                this.taxMethod = meta.sale.taxMethod
                this.sold = moment(meta.sale.sold || meta.sale.saleDate).format('yyyy-MM-DD')
            }

            this.maxApr = this.terms.get('maxApr').value
            this.dealerAPRLimit = this.terms.get('dealerAPRLimit').value
            this.lotId = meta.lotId
            this.initialized = true

            this.ms.channelComponents$.emit({ message: 'termsRead', section: 'terms', value: this.rawTerms })
        })
    }

    // ======================
    // Save the terms to the database.
    // ======================
    protected save = () => {
        if (!this.isUnLock) {
            return
        }

        if (this.calculating || this.loading || !this.initialized) {
            return
        }

        if (!this.saleId || (this.saleId <= 0)) {
            return
        }

        const identity = this.terms.get('id').value
        if (!identity || isNaN(identity) || (identity <= 0)) {
            return
        }

        const payload = {
            paymentCycleId: this.terms.get('paymentCycleId').value,
            schedulePmtAmt: clean(this.terms.get('payment').value),
            APR: clean(this.terms.get('apr').value),
            maxApr: this.terms.get('maxApr').value,
            amountToFinance: this.terms.get('financed').value,
            taxToFinance: 0,
            financeCharges: this.terms.get('interest').value,
            lastPmtAmt: this.terms.get('final').value,
            totalPaymentAmount: this.terms.get('payments').value,
            numberOfPmt: this.terms.get('term').value,
            months: this.toMonths(this.terms.get('term').value),
            firstScheduledPmtDue: this.terms.get('first').value,
            maturity: this.terms.get('maturity').value,
            dealerAPRLimit: this.terms.get('dealerAPRLimit').value || 0,
            contractRate: this.terms.get('contractRate').value,
            dailyAPR: this.terms.get('dailyAPR').value,
            taxRatio: 0,
            totalDownPayment: this.terms.get('totalDownPayment').value,
            taxRate: this.terms.get('taxRate').value || 0
        }

        if (this.taxMethod === 'DEFERRED') {
            payload.taxToFinance = this.salesTax
            payload.taxRatio = this.terms.get('taxRatio').value
        }

        this.master.put(`sales/${this.saleId}/terms/${identity}`, payload, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }
        })
    }

    /**
    *
    * @param {String} [solve = 'payment'] One of payment, term or apr to tell the compute engine what to solve for.
    */
    private calculate = async (solve = 'payment') => {
        if (!this.isUnLock) {
            console.log(`TermsComponent.calculate: isUnLock is false, skipping calculations.`)
            return
        }

        if (this.calculating || this.loading || !this.initialized) {
            console.log(`TermsComponent.calculate: calculating=${this.calculating}, loading=${this.loading}, initialized=${this.initialized}, skipping calculations.`)
            return
        }

        if (!this.saleId || (this.saleId <= 0)) {
            console.log(`TermsComponent.calculate: saleId=${this.saleId}, skipping calculations.`)
            return
        }

        if (!this.sold || (this.sold.toString().toLowerCase() == 'invalid date')) {
            console.log(`TermsComponent.calculate: sold=${this.sold}, skipping calculations.`)
            return
        }

        if (!this.termable) {
            console.log(`TermsComponent.calculate: saleType=${this.saleType} is not termable, skipping calculations.`)
            return
        }

        if ((this.baseSalePrice <= 0) || (this.amountToFinance <= 0)) {
            console.log(`TermsComponent.calculate: baseSalePrice=${this.baseSalePrice}, amountToFinance=${this.amountToFinance}, skipping calculations.`)
            return
        }

        if (!this.taxMethod || (this.taxMethod.length <= 0)) {
            console.log(`TermsComponent.calculate: taxMethod=${this.taxMethod}, skipping calculations.`)
            return
        }

        if (!this.saleVehicleId || (this.saleVehicleId <= 0)) {
            console.log(`TermsComponent.calculate: saleVehicleId=${this.saleVehicleId}, skipping calculations.`)
            return
        }

        this.calculating = true

        let changed = ''
        for (const key in this.terms.controls) {
            const control = this.terms.get(key)
            if (control && control.pristine) {
                continue
            }

            changed = key
            break
        }

        if (changed == '') {
            console.log(`TermsComponent.calculate: no changes detected, skipping calculations.`)
            this.calculating = false
            return
        }

        let desiredPayment = clean(this.terms.get('payment').value)
        if (!desiredPayment || isNaN(desiredPayment) || (desiredPayment <= 0)) {
            desiredPayment = 0
        }

        let financed = this.amountToFinance
        if (this.taxDeferred) {
            financed -= this.salesTax
        }

        if (!financed || (financed <= 0)) {
            console.log(`TermsComponent.calculate: financed=${financed}, skipping calculations.`)
            this.calculating = false
            return
        }

        if (this.terms.get('financed').value != financed) {
            this.terms.get('financed').setValue(financed, { emitEvent: false })
            this.terms.get('financed').markAsPristine()
        }

        const apr = clean(this.terms.get('apr').value)
        if ((apr === undefined) || (apr === null) || isNaN(apr) || (apr < 0)) {
            this.calculating = false
            return
        }

        const term = this.terms.get('term').value
        if (!term || isNaN(term) || (term <= 0)) {
            this.calculating = false
            return
        }

        let first = this.terms.get('first').value
        if (!first || (first.length <= 0)) {
            this.calculating = false
            return
        }

        if (moment(first).isBefore(this.sold)) {
            this.computeFirstPayDate()
            first = this.terms.get('first').value
        }

        first = encodeURIComponent(first.trim())

        const cycleId = this.terms.get('paymentCycleId').value
        const frequency = this.toFrequency(cycleId)
        if (!frequency) {
            this.calculating = false
            return
        }

        let payment = clean(this.terms.get('payment').value)
        if (!payment || isNaN(payment) || (payment <= 0)) {
            payment = 0
        }

        if ((payment <= 0) && (solve.toLowerCase() === 'term')) {
            this.calculating = false
            return
        }

        this.terms.disable({ emitEvent: false })

        const res = await this.master.getAsync(`vendors/terms/compute?salePrice=${this.baseSalePrice}&taxable=${this.totalSalePrice}&saleId=${this.saleId}&taxMethod=${this.taxMethod}&financed=${financed}&apr=${apr}&frequency=${frequency}&periods=${term}&sold=${this.sold}&first=${first}&solve=${solve}&changed=${changed}&pay=${payment}`)
            .catch(error => {
                this.ms.sendMessage("alert", { type: "danger", text: error })
                this.calculating = false
                this.terms.enable({ emitEvent: false })
                return
            })

        if (!res || !res.data) {
            this.calculating = false
            this.terms.enable({ emitEvent: false })

            let error = this.words[this.language]['apiNoResponse']
            if (res && res['error']) {
                error = res['error']
            }

            this.ms.sendMessage("alert", { type: "danger", text: error })
            return
        }

        this.terms.patchValue({
            financed: res.data.financed,
            interest: res.data.interest,
            apr: formatPercent(res.data.apr),
            paymentCycleId: this.toCycleId(res.data.frequency),
            term: res.data.count,
            payment: formatCurrency(res.data.payment),
            final: res.data.final,
            first: moment(res.data.first).format('yyyy-MM-DD'),
            maturity: res.data.last,
            payments: res.data.payments,
            months: this.toMonths(res.data.count),
            maxApr: res.data.maxLegalAPR,
            dealerAPRLimit: res.data.dealerAPRLimit,
            contractRate: res.data.contractRate,
            dailyAPR: res.data.dailyAPR,
            taxRatio: res.data.taxRatio,
            totalDownPayment: res.data.totalDownPayment,
            taxRate: res.data.taxRate
        }, { emitEvent: false, onlySelf: true })

        this.maxApr = res.data.maxLegalAPR
        this.dealerAPRLimit = res.data.dealerAPRLimit

        this.terms.enable({ emitEvent: false })
        this.calculating = false
        this.save()
    }

    /**
    * Handle input blur or change events
    * and recalculate when needed.
    * @param e
    * @returns
    */
    protected changed(e): void {
        if (this.terms.pristine) {
            return
        }

        if (this.calculating) {
            return
        }

        if (!e || !e.target || !e.target.id) {
            return
        }

        const whatChanged = e.target.id

        if (['months', 'maxApr'].includes(whatChanged)) { // dont need to recalc for these
            return
        }

        const input = this.terms.get(whatChanged)
        if (input) {
            this.terms.markAsPristine()
            input.markAsDirty()
        }

        this.solveFor = 'payment'

        switch (whatChanged) {
            case 'payment':
                this.solveFor = 'term'
                break
            case 'term':
                this.terms.get('months').setValue(this.toMonths(this.terms.get('term').value), { emitEvent: false })
                break
            case 'paymentCycleId':
                this.computeFirstPayDate(false)
                break
            case 'apr':
                if (!this.maxApr || (this.maxApr < 0)) {
                    break
                }

                const apr = parseFloat(e.target.value)
                if (!apr || (apr <= this.maxApr)) {
                    break
                }

                this.terms.get('apr').setValue(formatPercent(this.maxApr))
                break
        }

        this.calculate(this.solveFor)
    }

    /**
    * Handle lienholder choose events by the user.
    * @param lienholderId Unique internal identity of the lienholder contact chosen.
    */
    protected lienholderChosen(lienholderId: number, primary: boolean, index: number) {
        if (!lienholderId) {
            this.availableToSelect = !primary
            return
        }

        this.availableToSelect = true

        let id = 0
        if (this.lienholders && this.lienholders.length > 0) {
            if (this.lienholders[index]) {
                id = this.lienholders[index].id
            }
        }

        if (id && (id == lienholderId)) { // didn't change
            return
        }

        let lienholderIdExists = this.lienholders.find(lienholder => lienholder.lienholderContactId == lienholderId)
        if (lienholderIdExists) { // already a sale lienholder?
            return
        }

        if (id && (id > 0)) {
            this.modifySaleLienholder(id, lienholderId, primary)
            return
        }

        this.addSaleLienholder(lienholderId, primary)
    }

    /**contactId
    * Modfy the existing sale lienholder based on the recent choice by the user.
    * @param lienholder Unique internal identity of the current sale lienholder
    * @param lienholderId Unique internal identity of the new sale lienholder as chosen by the user
    */
    private modifySaleLienholder(id: number, lienholderId: number, primary: boolean) {
        if (!this.saleId) {
            return
        }

        if (!id || (id <= 0) || !lienholderId || (lienholderId <= 0)) {
            return
        }

        const payload = {
            lienholderContactId: lienholderId,
            lienholderLotId: null,
            address: null,
            bankFee: 0.0,
            buyRate: 0.0,
            splitRate: 0.0,
            primary
        }

        this.master.put(`sales/${this.saleId}/lienholders/${id}`, { payload: payload }, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.fetchLienholders()
        })
    }

    /**
    * Create a new sale lienholder based on the recent choice by the user.
    * @param lienholderId
    */
    private addSaleLienholder(lienholderId: number, primary: boolean) {
        if (!this.saleId) {
            return
        }

        if (!lienholderId || (lienholderId <= 0)) {
            return
        }

        const payload = {
            contactId: lienholderId,
            address: null,
            bankFee: 0.0,
            buyRate: 0.0,
            splitRate: 0.0,
            primary: primary || (this.lienholders.length <= 0)
        }

        this.master.post(`sales/${this.saleId}/lienholders`, payload, res => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] })
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error })
                return
            }

            this.fetchLienholders()
        })
    }

    /**
    *
    * @param paymentCycleId
    * @returns
    */
    private toFrequency(paymentCycleId: number): string | null {
        const found = this.frequencies.find((item) => item.id == paymentCycleId)
        if (!found || (!found.name && !found.carletonApiName)) {
            return null
        }

        return found.carletonApiName || found.name
    }

    /**
    *
    * @param frequency
    * @returns
    */
    private toCycleId(frequency: string): number {
        if (!frequency || (frequency.length <= 0)) {
            return 0
        }

        const found = this.frequencies.find((item) => ((item.name.toLowerCase() == frequency.toLowerCase()) || (item.carletonApiName.toLowerCase() == frequency.toLowerCase())))
        if (!found || !found.id) {
            return 0
        }

        const id = found.id
        if (!id || isNaN(id) || (id <= 0)) {
            return 0
        }

        return id
    }

    /**
    * Calculate the given term in months based on
    * the current payment frequency.
    * @param term The current number of payments to determine months with.
    * @returns The number of months in the given term and known frequency (cycle).
    */
    private toMonths(term: number): number {
        const frequency = this.toFrequency(this.terms.get('paymentCycleId').value)
        if (!frequency || frequency.length <= 0) {
            return term
        }

        switch (frequency.toLowerCase()) {
            case 'weekly': // once a week
                return Math.round((term / 52) * 12)
            case 'bi-weekly': // every two weeks
            case 'biweekly':
                return Math.round((term / 26) * 12)
            case 'semi-monthly': // twice a month
            case 'semimonthly':
                return Math.round(term / 2)
            case 'bi-monthly': // every two months
                return Math.round(term * 2)
            case 'quaterly': // every three months
                return Math.round(term * 3)
            case 'annually':
                return Math.round(term * 12)
            default: // once a month (i.e. monthly)
                return term
        }
    }

    /**
    * When the sale date or a down/deferred payment
    * changes lets compute the new first payment
    * date based on the currently selected
    * frequency (cycle).
    * @param {boolean} [calculate=true] Optional value to tell the method to (true) or not to (false) calculate.
    */
    private computeFirstPayDate(calculate: boolean = true): void {
        if (!this.isUnLock) {
            return
        }

        if (!this.lastDeferredPayment) {
            return
        }

        const cycleId = this.terms.get('paymentCycleId').value
        if (!cycleId || isNaN(cycleId) || (cycleId <= 0)) {
            return
        }

        const frequency = this.toFrequency(cycleId)
        if (!frequency || frequency.length <= 0) {
            return
        }

        const firstPayDate = this.terms.get('first').value
        if (!firstPayDate || (firstPayDate.length <= 0)) {
            return
        }

        let computed = null

        switch (frequency.toLowerCase()) {
            case 'weekly': // once a week
                computed = moment(this.lastDeferredPayment).add(1, 'week').format('yyyy-MM-DD')
                break
            case 'bi-weekly': // every two weeks
            case 'biweekly':
                computed = moment(this.lastDeferredPayment).add(2, 'weeks').format('yyyy-MM-DD')
                break
            case 'semi-monthly': // twice a month
            case 'semimonthly':
                computed = moment(this.lastDeferredPayment).add(15, 'days').format('yyyy-MM-DD')
                break
            case 'bi-monthly': // every two months
                computed = moment(this.lastDeferredPayment).add(2, 'months').format('yyyy-MM-DD')
                break
            case 'quaterly': // every three months
                computed = moment(this.lastDeferredPayment).add(3, 'months').format('yyyy-MM-DD')
                break
            case 'annually':
                computed = moment(this.lastDeferredPayment).add(1, 'year').format('yyyy-MM-DD')
                break
            default: // once a month (i.e. monthly)
                computed = moment(this.lastDeferredPayment).add(1, 'month').format('yyyy-MM-DD')
                break
        }

        if (!computed || (computed == firstPayDate)) { // no change?
            return
        }

        this.terms.get('first').setValue(computed, { emitEvent: false })

        if (!calculate || this.calculating) {
            return
        }

        this.terms.get('first').markAsDirty()
        this.calculate()
    }

    protected discardLienholder = (saleLienholderId) => {
        if (!this.saleId || (this.saleId <= 0)) {
            return
        }

        if (!saleLienholderId || (saleLienholderId <= 0)) {
            return
        }

        this.master.discard(`sales/${this.saleId}/lienholders/${saleLienholderId}`, (res) => {
            if (!res) {
                this.ms.sendMessage("alert", { type: "danger", text: this.words[this.language]['apiNoResponse'] });
                return
            }

            if (res.status !== 200) {
                this.ms.sendMessage("alert", { type: "danger", text: res.data.error });
                return
            }

            this.fetchLienholders()
        })
    }

}
